import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import './OrderHistory.css';

interface OrderItem {
  productId: string;
  name: string;
  quantity: number;
  price: number;
}

interface ShippingAddress {
  fullName: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface PreferredDelivery {
  date: string;
  timeSlot: string;
  instructions?: string;
}

interface Order {
  id: string;
  userId: string;
  userName: string;
  items: OrderItem[];
  total: number;
  deliveryFee?: number;
  deliveryEta?: string;
  fulfillmentType: 'delivery' | 'pickup';
  shippingAddress?: ShippingAddress;
  preferredDelivery?: PreferredDelivery;
  status: 'pending' | 'delivery' | 'completed' | 'cancelled';
  uberStatus?: string;
  uberTrackingUrl?: string;
  uberPickupAt?: string;
  uberDropoffAt?: string;
  createdAt: string;
  updatedAt: string;
}

const OrderHistory: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [membershipDiscountPercent, setMembershipDiscountPercent] = useState(0);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data.orders);
      setMembershipDiscountPercent(response.data.membershipDiscountPercent || 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'pending':
        return 'status-pending';
      case 'delivery':
        return 'status-delivery';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getOrderSavings = (order: Order) => {
    if (!membershipDiscountPercent || membershipDiscountPercent <= 0) return 0;
    return order.total / (1 - membershipDiscountPercent / 100) - order.total;
  };

  const toggleOrder = (orderId: string) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="orders-skeleton">
          {[0, 1, 2].map((item) => (
            <div key={item} className="order-skeleton-card">
              <div className="order-skeleton-line title" />
              <div className="order-skeleton-line meta" />
              <div className="order-skeleton-line total" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="order-history-header">
        <h1>Order History</h1>
        <div className="status-filter">
          <label>Filter by Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Orders</option>
            <option value="pending">Pending</option>
            <option value="delivery">Delivery</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <div className="no-orders-icon">📦</div>
          <h2>{orders.length === 0 ? 'No orders yet' : 'No orders match this status'}</h2>
          <p>
            {orders.length === 0
              ? "You haven't placed any orders yet."
              : 'Try a different status to view your orders.'}
          </p>
          <div className="no-orders-actions">
            {orders.length === 0 ? (
              <Link to="/products" className="btn btn-primary">
                Start shopping
              </Link>
            ) : (
              <button type="button" className="btn btn-secondary" onClick={() => setStatusFilter('all')}>
                Clear filter
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map((order) => (
            <div key={order.id} className="order-card">
              <button
                type="button"
                className="order-summary"
                onClick={() => toggleOrder(order.id)}
                aria-expanded={expandedOrders.has(order.id)}
              >
                <div>
                  <div className="order-summary-title">
                    Order #{order.id.slice(0, 8).toUpperCase()}
                  </div>
                  <div className="order-summary-meta">
                    <span>
                      {new Date(order.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                    <span className={`order-status ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                </div>
                <div className="order-summary-right">
                  <div className="order-summary-total">
                    Total: ${order.total.toFixed(2)}
                  </div>
                  <div className="order-summary-savings">
                    Savings: ${getOrderSavings(order).toFixed(2)}
                  </div>
                  <div className="order-summary-toggle">
                    {expandedOrders.has(order.id) ? 'Hide details' : 'View details'}
                  </div>
                </div>
              </button>

              {expandedOrders.has(order.id) && (
                <div className="order-details">
                  <div className="order-items">
                    <table className="order-items-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.name}</td>
                            <td>{item.quantity}</td>
                            <td>${item.price.toFixed(2)}</td>
                            <td>${(item.price * item.quantity).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="order-shipping-info">
                    <h4>Fulfillment</h4>
                    <div className="shipping-block">
                      <div className="shipping-label">
                        {order.fulfillmentType === 'delivery' ? 'Delivery' : 'Pickup'}
                      </div>
                    </div>
                    {order.fulfillmentType === 'delivery' && (order.uberStatus || order.uberTrackingUrl) && (
                      <div className="shipping-block">
                        <div className="shipping-label">Uber Direct</div>
                        {order.uberStatus && <div className="shipping-address">{order.uberStatus}</div>}
                        {order.uberPickupAt && (
                          <div className="shipping-address">
                            Picked up: {new Date(order.uberPickupAt).toLocaleString()}
                          </div>
                        )}
                        {order.uberDropoffAt && (
                          <div className="shipping-address">
                            Delivered: {new Date(order.uberDropoffAt).toLocaleString()}
                          </div>
                        )}
                        {order.uberTrackingUrl && (
                          <div className="shipping-address">
                            <a href={order.uberTrackingUrl} target="_blank" rel="noreferrer">
                              Track delivery
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {(order.fulfillmentType === 'delivery' &&
                    (order.shippingAddress || order.preferredDelivery)) && (
                    <div className="order-shipping-info">
                      <h4>Shipping Details</h4>
                      {order.shippingAddress && (
                        <div className="shipping-block">
                          <div className="shipping-name">{order.shippingAddress.fullName}</div>
                          <div className="shipping-address">
                            {order.shippingAddress.addressLine1}
                            {order.shippingAddress.addressLine2 ? `, ${order.shippingAddress.addressLine2}` : ''}
                          </div>
                          <div className="shipping-address">
                            {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}
                          </div>
                          <div className="shipping-address">{order.shippingAddress.country}</div>
                          <div className="shipping-phone">{order.shippingAddress.phone}</div>
                        </div>
                      )}
                      {order.preferredDelivery && (
                        <div className="shipping-block">
                          <div className="shipping-label">Preferred Delivery</div>
                          <div className="shipping-address">
                            {new Date(order.preferredDelivery.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </div>
                          <div className="shipping-address">{order.preferredDelivery.timeSlot}</div>
                          {order.preferredDelivery.instructions && (
                            <div className="shipping-notes">{order.preferredDelivery.instructions}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="order-footer">
                    {membershipDiscountPercent > 0 && (
                      <div className="order-savings">
                        <span>Member Savings ({membershipDiscountPercent}%)</span>
                        <strong>
                          ${getOrderSavings(order).toFixed(2)}
                        </strong>
                      </div>
                    )}
                    {order.fulfillmentType === 'delivery' && order.deliveryFee !== undefined && (
                      <div className="order-savings">
                        <span>Delivery Fee</span>
                        <strong>${order.deliveryFee.toFixed(2)}</strong>
                      </div>
                    )}
                    {order.fulfillmentType === 'delivery' && order.deliveryEta && (
                      <div className="order-savings">
                        <span>Estimated Delivery</span>
                        <strong>{new Date(order.deliveryEta).toLocaleString()}</strong>
                      </div>
                    )}
                    <div className="order-total">
                      <strong>Total: ${order.total.toFixed(2)}</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
